// The Boot server page (spec/25 §16.1): the boot server's status in a sentence with its software and drives (`ServersTab`),
// how its PCs are doing (`HealthTab`, once a server is paired) and the venue's Clavian Boot settings (`BootSettingsTab`),
// one under the other so a member who looks for "the server" finds all of it in one place.

import type { BootServer } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useId, type ReactNode } from 'react';

import type { MyVenue } from '../api/me';
import type { BootAccess } from './BootPage';
import { BootSettingsTab } from './BootSettingsTab';
import { HealthTab } from './HealthTab';
import type { BootScope } from './queries';
import { ServersTab } from './ServersTab';

export function ServerPage({
  venue,
  scope,
  servers,
  access,
}: {
  readonly venue: MyVenue;
  readonly scope: BootScope;
  readonly servers: UseQueryResult<BootServer[], unknown>;
  readonly access: BootAccess;
}): ReactNode {
  const { t } = useTranslation('console');
  const healthId = useId();
  const settingsId = useId();
  const server = servers.data?.[0];
  const paired = server !== undefined && server.status !== 'pairing';
  return (
    <div className="console-boot__stack">
      <ServersTab scope={scope} servers={servers} access={access} />
      {paired ? (
        <section className="console-boot__block" aria-labelledby={healthId}>
          <h2 id={healthId} className="console-boot__subhead">
            {t('boot.health.title')}
          </h2>
          <HealthTab scope={scope} />
        </section>
      ) : null}
      <section className="console-boot__block" aria-labelledby={settingsId}>
        <h2 id={settingsId} className="console-boot__subhead">
          {t('boot.settings.title')}
        </h2>
        <BootSettingsTab venue={venue} />
      </section>
    </div>
  );
}
